import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WhyESBSection = () => {
  const differentiators = [
    {
      icon: "Package",
      title: "Product Range & Quality",
      description: "Comprehensive portfolio of premium endoscopic and surgical instruments from world-renowned manufacturers, ensuring healthcare professionals have access to the latest medical technology.",
      features: [
        "Latest endoscopic equipment",
        "Surgical instruments",
        "Imaging systems",
        "Quality accessories"
      ],
      image: "/assets/images/Product Range & Quality.jpg",
      color: "primary"
    },
    {
      icon: "Building",
      title: "Facilities & Resources",
      description: "State-of-the-art distribution facilities with advanced inventory management, technical support centers, and comprehensive training programs for medical professionals.",
      features: [
        "Modern warehouse facilities",
        "Technical support center",
        "Training programs",
        "Inventory management"
      ],
      image: "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=600&h=400&fit=crop",
      color: "success"
    },
    {
      icon: "DollarSign",
      title: "Best Value Price & Services",
      description: "Competitive pricing strategies combined with exceptional after-sales support, maintenance services, and flexible payment options tailored for healthcare facilities.",
      features: [
        "Competitive pricing",
        "After-sales support",
        "Maintenance services",
        "Flexible payment terms"
      ],
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop",
      color: "accent"
    },
    {
      icon: "Users",
      title: "Symbiotic Relationships",
      description: "Building long-term partnerships with healthcare providers through collaborative approach, understanding unique needs, and providing customized solutions for optimal patient care.",
      features: [
        "Long-term partnerships",
        "Collaborative approach",
        "Customized solutions",
        "Ongoing support"
      ],
      image: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=600&h=400&fit=crop",
      color: "warning"
    }
  ];

  const additionalBenefits = [
    {
      icon: "GraduationCap",
      title: "Continuous Medical Education",
      description: "Regular training sessions and workshops to keep medical professionals updated with latest techniques and technologies."
    },
    {
      icon: "Headphones",
      title: "24/7 Technical Support",
      description: "Round-the-clock technical assistance and emergency support for critical medical equipment."
    },
    {
      icon: "Truck",
      title: "Nationwide Delivery",
      description: "Efficient logistics network ensuring timely delivery across all states in Malaysia."
    },
    {
      icon: "Shield",
      title: "Warranty & Compliance",
      description: "Comprehensive warranty coverage and full compliance with Malaysian medical device regulations."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Why Choose Endosurgery Malaysia?
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Four key differentiators that make us Malaysia's preferred partner for medical technology solutions.
          </p>
        </motion.div>

        {/* Main Differentiators */}
        <div className="space-y-20 mb-20">
          {differentiators?.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
            >
              <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className={`w-16 h-16 bg-${item?.color}/10 rounded-xl flex items-center justify-center`}>
                      <Icon name={item?.icon} size={28} color={`var(--color-${item?.color})`} />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{item?.title}</h3>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">{item?.description}</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {item?.features?.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} color={`var(--color-${item?.color})`} />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                <div className="relative group">
                  <Image
                    src={item?.image}
                    alt={item?.title}
                    className="rounded-2xl shadow-medical group-hover:shadow-medical-hover transition-all duration-300 w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  <div className={`absolute bottom-4 left-4 bg-${item?.color} text-white px-4 py-2 rounded-lg font-medium text-sm`}>
                    {item?.title}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 shadow-medical border border-gray-200 dark:border-gray-600"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Additional Benefits</h3>
            <p className="text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Comprehensive support services that ensure your success with our medical technology solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalBenefits?.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group cursor-pointer"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon 
                    name={benefit?.icon} 
                    size={24} 
                    className="text-primary group-hover:text-white transition-colors duration-300"
                  />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-primary transition-colors duration-300">{benefit?.title}</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300">{benefit?.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Ready to Partner with Us?</h3>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">
              Join over 500 medical professionals who trust Endosurgery Malaysia for their equipment needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors duration-200"
                onClick={() => window.location.href = '/product-portfolio'}
              >
                View Products
              </button>
              <button 
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-all duration-200"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Us Today
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyESBSection;